import { Add as AddIcon, CloudUpload as CloudUploadIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import isEqual from 'lodash/isEqual';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import * as yup from 'yup';

import * as voiceLib from 'entities/voice/lib';

import {
  Country,
  LocalizedText,
  SocialLinkType,
  SocialLinks,
  VOICE_NOTES_MAX_LENGTH,
  VoiceStatus,
  VoiceTarget,
  Voices,
  typicodeApi,
} from 'shared/api';
import { LANGUAGE_OPTIONS } from 'shared/config';
import { AutocompleteCountry } from 'shared/ui';

import { LanguageContent } from './language-content';

const ACCEPTED_IMAGE_TYPES = 'image/jpeg,image/png';

const validationSchema = yup.object({
  _id: yup.string(),
  name: yup.string().required('Required field'),
  countryCode: yup.string().max(2, 'Max 2 characters').required('Required field'),
  // Языки самого автора. К табам описания отношения не имеют.
  languages: yup.array().of(yup.string().required()).min(1, 'At least one language required'),
  // Языки, для которых открыт таб описания. На сервер не отправляется.
  tabLanguages: yup.array().of(yup.string().required()).min(1),
  avatar: yup.string().required('Avatar is required'),
  bio: yup
    .array()
    .of(yup.object({ language: yup.string().required(), text: yup.string().defined() }))
    .test('bio-complete', 'Bio is required for every description language', function (value) {
      const tabLanguages = (this.parent as { tabLanguages?: string[] }).tabLanguages ?? [];
      const missing = voiceLib.getMissingBioLanguages(tabLanguages, value as LocalizedText[] | undefined);
      if (missing.length) {
        return this.createError({
          message: `Bio is required for: ${voiceLib.formatMissingBioLanguages(missing)}`,
        });
      }
      // Сервер требует запись en в bio (@ContainsLanguage('en')) независимо от tabLanguages.
      if (!voiceLib.getLocalizedText(value as LocalizedText[] | undefined, 'en').trim()) {
        return this.createError({ message: 'Bio is required for English' });
      }
      return true;
    }),
  specialty: yup.array().of(yup.object({ language: yup.string().required(), text: yup.string().defined() })),
  target: yup.mixed<VoiceTarget>().oneOf([VoiceTarget.Pets, VoiceTarget.People]).required('Required field'),
  status: yup.mixed<VoiceStatus>().oneOf(Object.values(VoiceStatus)).required('Required field'),
  isPublished: yup.boolean().required(),
  notes: yup.string().max(VOICE_NOTES_MAX_LENGTH, `Max ${VOICE_NOTES_MAX_LENGTH} characters`),
  socialLinks: yup.array().of(
    yup.object({
      type: yup.mixed<SocialLinkType>().oneOf(Object.values(SocialLinkType)).required('Required'),
      url: yup.string().url('Must be a valid URL').required('Required'),
    }),
  ),
});

type Props = {
  title: string;
  isOpen: boolean;
  voice?: Voices;
  onClose: () => void;
  onSubmit: (data: Partial<Voices>) => void;
  isLoading: boolean;
  countries: Country[];
};

const emptySocialLink: SocialLinks = {
  type: SocialLinkType.Website,
  url: '',
};

const { isAvatarFile } = voiceLib;

/**
 * Набор табов описания = языки, встречающиеся в bio и specialty, плюс гарантированный
 * 'en' первым (бэк требует запись en в bio). languages[] сюда НЕ входит — это языки
 * самого автора, а не языки перевода описания.
 */
const buildTabLanguages = (voice?: Voices): string[] => {
  const collected = new Set<string>();
  voice?.bio?.forEach((item) => collected.add(item.language));
  voice?.specialty?.forEach((item) => collected.add(item.language));
  return ['en', ...Array.from(collected).filter((lang) => lang !== 'en')];
};

type FormValues = {
  _id: string;
  name: string;
  countryCode: string;
  languages: string[];
  tabLanguages: string[];
  avatar: string;
  bio: LocalizedText[];
  specialty: LocalizedText[];
  target: VoiceTarget;
  status: VoiceStatus;
  isPublished: boolean;
  notes: string;
  socialLinks: SocialLinks[];
};

/** Единственное место, где voice превращается в значения формы: и для initialValues, и для resetForm. */
const buildFormValues = (voice?: Voices): FormValues => ({
  _id: voice?._id ?? '',
  name: voice?.name ?? '',
  countryCode: voice?.countryCode ?? '',
  languages: voice?.languages ?? [],
  tabLanguages: buildTabLanguages(voice),
  avatar: voice?.avatar ? (isAvatarFile(voice.avatar) ? voice.avatar._id : voice.avatar) : '',
  bio: voice?.bio ?? [],
  specialty: voice?.specialty ?? [],
  target: voice?.target ?? VoiceTarget.People,
  status: voiceLib.getVoiceStatus(voice?.status),
  // Новый автор по умолчанию черновик, как и на бэке; у существующего админский ответ всегда несёт boolean.
  isPublished: voice?.isPublished ?? false,
  notes: voice?.notes ?? '',
  socialLinks: voice?.socialLinks ?? [],
});

const sortByLanguage = (items: LocalizedText[]): LocalizedText[] =>
  [...items].sort((a, b) => a.language.localeCompare(b.language));

/**
 * То, что реально уходит на сервер. Используется и для payload, и для сравнения
 * «изменилось ли что-то» — чтобы семантика кнопки Update и PATCH не расходилась.
 * - tabLanguages — состояние формы, а не поле автора;
 * - _id в теле PATCH не нужен;
 * - записи с пустым text выбрасываем: specialty бэк отвергает, а пустой bio до submit
 *   не доживёт (Yup), но для сравнения «ввёл и стёр» должен считаться не-изменением;
 * - порядок локализаций смыслу не важен: удалили язык и вернули — не изменение.
 */
const normalizeValues = ({ _id, tabLanguages, ...rest }: FormValues): Omit<FormValues, '_id' | 'tabLanguages'> => ({
  ...rest,
  bio: sortByLanguage(rest.bio.filter((item) => item.text.trim())),
  specialty: sortByLanguage(rest.specialty.filter((item) => item.text.trim())),
  // Пробелы по краям — не изменение; заметка из одних пробелов — пустая (таблица её так и показывает).
  notes: rest.notes.trim(),
});

export const VoiceDialog = ({ title, isOpen, voice, onClose, onSubmit, isLoading, countries }: Props) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const bioInputRef = useRef<HTMLInputElement>(null);
  const [focusBioToken, setFocusBioToken] = useState(0);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);
  const [activeLanguage, setActiveLanguage] = useState('en');

  const formik = useFormik<FormValues>({
    initialValues: buildFormValues(voice),
    validationSchema,
    onSubmit: async (values) => {
      let avatarId = values.avatar;

      if (avatarFile) {
        try {
          setIsUploading(true);
          const response = await typicodeApi.files.uploadAvatar(avatarFile);
          avatarId = response.data.id;
        } catch {
          toast.error('Error uploading avatar. Try again!');
          setIsUploading(false);
          return;
        } finally {
          setIsUploading(false);
        }
      }

      const payload = normalizeValues(values);
      // Пустую заметку шлём только чтобы стереть существующую ('' — способ очистки на бэке),
      // иначе каждый новый или старый автор без заметки получал бы пустое поле.
      onSubmit({ ...payload, notes: payload.notes || (voice?.notes ? '' : undefined), avatar: avatarId });
    },
  });

  // Загрузка превью при открытии в режиме редактирования
  useEffect(() => {
    if (isOpen && voice?.avatar && isAvatarFile(voice.avatar)) {
      typicodeApi.files
        .getFileByPath(voice.avatar.path)
        .then((response) => {
          const url = URL.createObjectURL(response.data);
          setAvatarPreview(url);
        })
        .catch(() => {
          setAvatarPreview('');
        });
    }

    return () => {
      if (avatarPreview) {
        URL.revokeObjectURL(avatarPreview);
      }
    };
    // eslint-disable-next-line
  }, [voice, isOpen]);

  useEffect(() => {
    if (isOpen) {
      if (voice) {
        // resetForm, а не setValues: обновляет и initialValues — от него считается isChanged.
        formik.resetForm({ values: buildFormValues(voice) });
        setActiveLanguage('en');
        // Успешный Update закрывает диалог через onClose из EditVoice, минуя handleClose,
        // поэтому выбранный File мог пережить закрытие — сбрасываем при каждом открытии.
        setAvatarFile(null);
      } else {
        formik.resetForm();
        setActiveLanguage('en');
        setAvatarFile(null);
        setAvatarPreview('');
      }
    }
    // eslint-disable-next-line
  }, [voice, isOpen]);

  const handleClose = () => {
    formik.resetForm();
    // Иначе Tabs до следующего открытия держит value удалённой/добавленной вкладки — MUI ругается.
    setActiveLanguage('en');
    setAvatarFile(null);
    setAvatarPreview('');
    onClose();
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setAvatarFile(file);
    void formik.setFieldValue('avatar', 'pending-upload');

    const previewUrl = URL.createObjectURL(file);
    if (avatarPreview) {
      URL.revokeObjectURL(avatarPreview);
    }
    setAvatarPreview(previewUrl);

    // Сбрасываем input чтобы можно было выбрать тот же файл
    event.target.value = '';
  };

  const handleRemoveAvatar = () => {
    setAvatarFile(null);
    if (avatarPreview) {
      URL.revokeObjectURL(avatarPreview);
    }
    setAvatarPreview('');
    void formik.setFieldValue('avatar', '');
  };

  const handleAddSocialLink = () => {
    void formik.setFieldValue('socialLinks', [...formik.values.socialLinks, { ...emptySocialLink }]);
  };

  const handleRemoveSocialLink = (index: number) => {
    void formik.setFieldValue(
      'socialLinks',
      formik.values.socialLinks.filter((_, i) => i !== index),
    );
  };

  const isBusy = isLoading || isUploading;

  // Update активна только когда отправляемый payload отличается от исходного.
  // Стёр букву и вернул — не изменение; открыл пустую вкладку языка — тоже.
  const isChanged = useMemo(
    () => !isEqual(normalizeValues(formik.values), normalizeValues(formik.initialValues)),
    [formik.values, formik.initialValues],
  );

  const missingBioLanguages = voiceLib.getMissingBioLanguages(formik.values.tabLanguages, formik.values.bio);

  // Фокус на Bio переносим после того, как таб реально переключится и отрендерится —
  // синхронный focus() здесь попал бы в ещё не смонтированное поле неактивного таба.
  // Токен (а не activeLanguage) в зависимостях — чтобы эффект срабатывал и когда
  // невалидный таб уже был активен на момент сабмита.
  useEffect(() => {
    if (focusBioToken) {
      bioInputRef.current?.focus();
    }
  }, [focusBioToken]);

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <form
        onSubmit={(event) => {
          if (missingBioLanguages.length) {
            setActiveLanguage(missingBioLanguages[0]);
            setFocusBioToken((token) => token + 1);
          }
          formik.handleSubmit(event);
        }}
        style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        <DialogContent sx={{ overflowY: 'auto' }}>
          <Box display="flex" flexDirection="column" gap={2}>
            {/* Avatar */}
            <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
              <Avatar
                src={avatarPreview || undefined}
                sx={{ width: 120, height: 120, bgcolor: 'grey.300', fontSize: 48 }}
              >
                {!avatarPreview && formik.values.name?.[0]?.toUpperCase()}
              </Avatar>
              <Box display="flex" gap={1} alignItems="center">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CloudUploadIcon />}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {avatarPreview ? 'Change avatar' : 'Upload avatar'}
                </Button>
                {avatarPreview && (
                  <Button variant="text" size="small" color="error" onClick={handleRemoveAvatar}>
                    Remove
                  </Button>
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">
                JPEG, PNG
              </Typography>
              <input ref={fileInputRef} type="file" accept={ACCEPTED_IMAGE_TYPES} hidden onChange={handleFileSelect} />
              {formik.touched.avatar && formik.errors.avatar && (
                <FormHelperText error>{formik.errors.avatar}</FormHelperText>
              )}
            </Box>

            {/* Name */}
            <TextField
              fullWidth
              name="name"
              label="Name *"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
            />

            {/* Country Code */}
            <AutocompleteCountry
              label="Country *"
              value={formik.values.countryCode}
              countries={countries}
              onChange={(_: any, v: any) => formik.setFieldValue('countryCode', v?.code ?? '')}
              error={formik.touched.countryCode && Boolean(formik.errors.countryCode)}
              helperText={formik.touched.countryCode && formik.errors.countryCode}
            />

            {/* Languages */}
            <Autocomplete
              multiple
              options={LANGUAGE_OPTIONS.map((l) => l.code)}
              getOptionLabel={(option) => {
                const lang = LANGUAGE_OPTIONS.find((l) => l.code === option);
                return lang ? `${lang.name} (${lang.code})` : option;
              }}
              value={formik.values.languages}
              onChange={(_, newValue) => {
                void formik.setFieldValue('languages', newValue);
              }}
              freeSolo
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const lang = LANGUAGE_OPTIONS.find((l) => l.code === option);
                  return (
                    <Chip label={lang ? lang.name : option} size="small" {...getTagProps({ index })} key={option} />
                  );
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Languages *"
                  error={formik.touched.languages && Boolean(formik.errors.languages)}
                  helperText={formik.touched.languages && (formik.errors.languages as string)}
                />
              )}
            />

            {/* Target */}
            <FormControl fullWidth size="small" error={formik.touched.target && Boolean(formik.errors.target)}>
              <InputLabel id="voice-target-label">Target *</InputLabel>
              <Select
                labelId="voice-target-label"
                name="target"
                label="Target *"
                value={formik.values.target}
                onChange={formik.handleChange}
              >
                <MenuItem value={VoiceTarget.People}>People</MenuItem>
                <MenuItem value={VoiceTarget.Pets}>Pets</MenuItem>
              </Select>
              {formik.touched.target && formik.errors.target && <FormHelperText>{formik.errors.target}</FormHelperText>}
            </FormControl>

            <Divider />

            {/* Внутренние поля: в мобилку не отдаются */}
            <Typography variant="subtitle1">Internal</Typography>
            <Box display="flex" alignItems="center" gap={2}>
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel id="voice-status-label">Status</InputLabel>
                <Select
                  labelId="voice-status-label"
                  name="status"
                  label="Status"
                  value={formik.values.status}
                  onChange={formik.handleChange}
                >
                  {Object.values(VoiceStatus).map((value) => (
                    <MenuItem key={value} value={value}>
                      {voiceLib.VOICE_STATUS_LABELS[value]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Checkbox name="isPublished" checked={formik.values.isPublished} onChange={formik.handleChange} />
                }
                label="Published"
              />
            </Box>
            <TextField
              fullWidth
              multiline
              minRows={3}
              maxRows={10}
              name="notes"
              label="Notes"
              value={formik.values.notes}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={Boolean(formik.errors.notes)}
              helperText={formik.errors.notes ?? `${formik.values.notes.length}/${VOICE_NOTES_MAX_LENGTH}`}
            />

            <Divider />

            <LanguageContent
              tabLanguages={formik.values.tabLanguages}
              bio={formik.values.bio}
              specialty={formik.values.specialty}
              activeLanguage={activeLanguage}
              missingBioLanguages={missingBioLanguages}
              showErrors={!!formik.submitCount || !!formik.touched.bio}
              showSummary={!!formik.submitCount}
              onActiveLanguageChange={setActiveLanguage}
              onChange={(next) => {
                void formik.setValues({ ...formik.values, ...next });
              }}
              onBlur={() => formik.setFieldTouched('bio', true)}
              bioInputRef={bioInputRef}
            />

            {/* Social Links */}
            <Divider />
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="subtitle1">Social Links</Typography>
              <Button startIcon={<AddIcon />} size="small" onClick={handleAddSocialLink}>
                Add link
              </Button>
            </Box>

            {formik.values.socialLinks.map((link, index) => {
              const errors = formik.errors.socialLinks as SocialLinks[] | undefined;
              const touched = formik.touched.socialLinks as Array<Record<string, boolean>> | undefined;
              const linkErrors = errors?.[index];
              const linkTouched = touched?.[index];

              return (
                <Box key={index} display="flex" gap={1} alignItems="flex-start">
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Type</InputLabel>
                    <Select
                      name={`socialLinks[${index}].type`}
                      label="Type"
                      value={link.type}
                      onChange={formik.handleChange}
                    >
                      {Object.values(SocialLinkType).map((type) => (
                        <MenuItem key={type} value={type}>
                          {voiceLib.getSocialLinkTypeLabel(type)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    size="small"
                    name={`socialLinks[${index}].url`}
                    label="URL"
                    value={link.url}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={!!linkTouched?.url && !!linkErrors?.url}
                    helperText={linkTouched?.url && linkErrors?.url}
                    sx={{ flex: 1 }}
                  />

                  <IconButton size="small" color="error" onClick={() => handleRemoveSocialLink(index)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isBusy}>
            Cancel
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={isBusy}
            disabled={isBusy || (!!voice && !isChanged)}
          >
            {voice ? 'Update' : 'Create'}
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};
